import i18next from 'i18next';
import parse from 'html-react-parser';
import React, { useState, useEffect } from 'react';

// Commons
import { Dropdown } from '@commons/utils/styledLibraryComponents';
import { formatTextNumberInputValue } from '@commons/utils/format';
import {
  GENERIC_MODAL_CANCEL_BUTTON,
  GENERIC_MODAL_SAVE_BUTTON,
} from '@commons/Modals/GenericModal/genericModalActions';
import InputContentModal from '@commons/Modals/InputContentModal';
import Text, { ENUM_FONTS, ENUM_COLORS } from '@commons/Text';

import { Container, RowContainer } from './styledComponents';

export const useBufferStockModal = () => {
  const [nbDays, setNbDays] = useState(null);
  const [bufferStockStores, setBufferStockStores] = useState([]);

  return {
    nbDays,
    setNbDays,
    bufferStockStores,
    setBufferStockStores,
  };
};

const ModalContent = ({
  nbDays,
  setNbDays,
  bufferStockStores,
  setBufferStockStores,
  associatedStores,
  storeName,
  prevHorizon,
}) => {
  const [groups, setGroups] = useState([]);
  const [selectedGroups, setSelectedGroups] = useState([]);
  const [storesToDisplay, setStoresToDisplay] = useState(associatedStores);

  useEffect(() => {
    const allgroupsNamesSet = associatedStores.reduce((acc, { groups }) => {
      groups.forEach(({ name }) => {
        acc.add(name);
      });

      return acc;
    }, new Set());

    const allgroupsNames = [...allgroupsNamesSet];

    if (!allgroupsNames.length) {
      setGroups([]);
      setSelectedGroups([]);
      return;
    }

    allgroupsNames.push(i18next.t('GENERAL.SELECT_NONE_MASCULINE'));

    const allGroups = allgroupsNames.map((name, index) => ({ id: index, name }));

    setGroups(allGroups);
    setSelectedGroups(allGroups);
  }, []);

  useEffect(() => {
    if (!groups.length) {
      setStoresToDisplay(associatedStores);
      setBufferStockStores(associatedStores);
      return;
    }

    const filteredStores = associatedStores.filter(({ groups: storeGroups }) => {
      if (!!storeGroups.length) {
        return storeGroups.some(({ name }) =>
          selectedGroups.some(({ name: selectedName }) => selectedName === name),
        );
      }

      return selectedGroups.some(({ name }) => name === i18next.t('GENERAL.SELECT_NONE_MASCULINE'));
    });

    setStoresToDisplay(filteredStores);
    setBufferStockStores(filteredStores);
  }, [selectedGroups]);

  return (
    <Container>
      <Text color={ENUM_COLORS.DARKER} font={ENUM_FONTS.TEXT_BIG_HEIGHT_24}>
        {i18next.t('ADMIN.INGREDIENTS.BUFFER_STOCKS_MODAL_TEXT')}
      </Text>
      <RowContainer>
        {!!groups.length && (
          <Dropdown
            customStyle={{ position: 'inherit' }}
            height={'68px'}
            iconSrc={'/images/inpulse/store-black-small.svg'}
            isUniqueSelection={false}
            items={groups}
            label={i18next.t('GENERAL.GROUPS')}
            selectedItems={selectedGroups}
            isRequired
            onSelectionChange={setSelectedGroups}
          />
        )}
        <Dropdown
          customStyle={{ position: 'inherit' }}
          height={'68px'}
          iconSrc={'/images/inpulse/store-black-small.svg'}
          isUniqueSelection={false}
          items={storesToDisplay}
          label={storeName}
          selectedItems={bufferStockStores}
          isRequired
          onSelectionChange={setBufferStockStores}
        />
        <InputContentModal
          autoFocus={true}
          errorMessage={
            nbDays > prevHorizon.future &&
            parse(
              i18next.t('ADMIN.INGREDIENTS.BUFFER_STOCK_NB_DAYS_HIGHER_THAN_PREV_HORIZON_ERROR', {
                prevHorizon: prevHorizon.future,
              }),
            )
          }
          label={i18next.t('FEATURE_NAME_ADMIN_STORES_BUFFER_STOCKS')}
          placeholder={i18next.t('FEATURE_NAME_ADMIN_STORES_BUFFER_STOCKS')}
          tooltipText={i18next.t('ADMIN.STORES.BUFFER_STOCK_COLUMN_TOOLTIP')}
          type="text"
          value={nbDays === null ? '' : nbDays}
          onChange={(value) => {
            const formattedValue = formatTextNumberInputValue(value, null);

            setNbDays(
              formattedValue !== null && formattedValue >= 0 ? Math.trunc(formattedValue) : null,
            );
          }}
        />
      </RowContainer>
    </Container>
  );
};

export const getBufferStockModalConfig = ({
  handleBufferStockModalSave,
  nbDays,
  setNbDays,
  bufferStockStores,
  setBufferStockStores,
  associatedStores,
  storeName,
  prevHorizon,
}) => ({
  type: 'action',
  width: '560px',
  height: 'auto',
  maxHeight: '480px',
  icon: '/images/inpulse/gear-ipblack-small.svg',
  title: i18next.t('ADMIN.STORES.MANAGE_BUFFER_STOCKS_ACTIONS'),
  data: {
    nbDays,
    setNbDays,
    bufferStockStores,
    setBufferStockStores,
    associatedStores,
    storeName,
    prevHorizon,
  },
  component: ModalContent,
  handleCloseCleanUp: () => {
    setNbDays(null);
    setBufferStockStores(associatedStores);
  },
  actions: [
    GENERIC_MODAL_CANCEL_BUTTON(),
    {
      ...GENERIC_MODAL_SAVE_BUTTON(),
      isDisabled:
        nbDays === null ||
        nbDays === undefined ||
        nbDays < 0 ||
        !bufferStockStores.length ||
        nbDays > prevHorizon.future,
      handleClick: handleBufferStockModalSave,
    },
  ],
});
